// Landscape phone layout for Transfers (Option C)

import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TransferEditFab from './TransferEditFab';
import { faChartPie } from '@fortawesome/free-solid-svg-icons';
import FortnightRangeControl from './FortnightRangeControl';
import { getMobilePillAppearance, formatPillLabel } from './transferMobilePills';
import '../../css/PhoneLandscapeTransferView.css';

const formatPayDateLandscape = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

const PhoneLandscapeTransferView = ({
  payData,
  bankAccounts,
  fortnightIndex,
  setFortnightIndex,
  formattedRangeFortnight,
  editTransfers,
  setEditTransfers,
  onTogglePaid,
  onCellEditClick,
  onJumpToCurrentFortnight,
}) => {
  const totalFortnightlyRequirements = useMemo(
    () =>
      bankAccounts.reduce(
        (total, account) => total + parseFloat(account.required_amount || 0),
        0
      ),
    [bankAccounts]
  );

  const totalPays = useMemo(
    () => payData.reduce((sum, pay) => sum + parseFloat(pay.amount || 0), 0),
    [payData]
  );

  const handlePillClick = (payBankAccount, payId) => {
    if (!payBankAccount) return;
    const amount = parseFloat(payBankAccount.transfer_amount);
    if (amount === 0 || payBankAccount.transfer_amount === '0.00') {
      if (!editTransfers) return;
    }
    if (editTransfers) {
      onCellEditClick(payBankAccount, payId);
      return;
    }
    onTogglePaid(payBankAccount);
  };

  return (
    <div className="phone-landscape-view">
      <FortnightRangeControl
        formattedRangeFortnight={formattedRangeFortnight}
        fortnightIndex={fortnightIndex}
        setFortnightIndex={setFortnightIndex}
        onJumpToCurrent={onJumpToCurrentFortnight}
        className="phone-fortnight-pill phone-landscape-fortnight"
        rangeClassName="phone-landscape-fortnight-range"
      />

      <div className="phone-landscape-scroll">
        <table className="phone-landscape-table">
          <thead>
            <tr>
              <th className="phone-landscape-account-col">Account</th>
              {payData.map((pay) => (
                <th key={pay.id} className="phone-landscape-pay-col">
                  <div className="phone-landscape-pay-card">
                    <div className="phone-landscape-pay-card-name">
                      {pay.nickname}
                      {pay.canUseRemaining && (
                        <FontAwesomeIcon
                          icon={faChartPie}
                          className="phone-landscape-pay-card-icon"
                        />
                      )}
                    </div>
                    <div className="phone-landscape-pay-card-date">
                      {formatPayDateLandscape(pay.next_pay)}
                    </div>
                    <div className="phone-landscape-pay-card-amount">
                      ${parseFloat(pay.amount).toFixed(2)}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bankAccounts.map((account) => {
              const requiredAmount = parseFloat(account.required_amount);
              const totalTransfers = payData
                .flatMap((pay) => pay.bank_accounts)
                .filter((ba) => ba.bank_account === account.id)
                .reduce((t, ba) => t + parseFloat(ba.transfer_amount), 0);
              const difference = totalTransfers - requiredAmount;

              return (
                <tr key={account.id}>
                  <td className="phone-landscape-account-col">
                    <div className="phone-landscape-account-name">{account.nickname}</div>
                    <div className="phone-landscape-account-req">
                      ${requiredAmount.toFixed(2)} req
                    </div>
                  </td>
                  {payData.map((pay) => {
                    const payBankAccount = pay.bank_accounts.find(
                      (ba) => ba.bank_account === account.id
                    );
                    const { className: pillClass } = getMobilePillAppearance(payBankAccount);
                    const isZero =
                      !payBankAccount ||
                      payBankAccount.transfer_amount === '0.00' ||
                      parseFloat(payBankAccount?.transfer_amount || 0) === 0;

                    return (
                      <td key={pay.id} className="phone-landscape-pay-col">
                        <button
                          type="button"
                          className={`phone-landscape-pill ${pillClass}`}
                          onClick={() => handlePillClick(payBankAccount, pay.id)}
                          disabled={!editTransfers && isZero}
                        >
                          {formatPillLabel(payBankAccount)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="phone-landscape-footer">
        <div className="phone-landscape-totals">
          <span>
            Total required: <strong>${totalFortnightlyRequirements.toFixed(2)}</strong>
          </span>
          <span>
            Total pays: <strong>${totalPays.toFixed(2)}</strong>
          </span>
          <span
            className={
              totalPays - totalFortnightlyRequirements < 0
                ? 'phone-landscape-totals--under'
                : totalPays - totalFortnightlyRequirements > 0
                ? 'phone-landscape-totals--over'
                : ''
            }
          >
            Difference:{' '}
            <strong>
              {(totalPays - totalFortnightlyRequirements === 0
                ? '0.00'
                : `${totalPays - totalFortnightlyRequirements < 0 ? '−' : '+'}$${Math.abs(
                    totalPays - totalFortnightlyRequirements
                  ).toFixed(2)}`)}
            </strong>
          </span>
        </div>

        <div className="phone-landscape-key">
          <div className="phone-key-item">
            <span className="phone-key-swatch" style={{ backgroundColor: '#d4edda' }} />
            <span>Paid</span>
          </div>
          <div className="phone-key-item">
            <span className="phone-key-swatch" style={{ backgroundColor: '#f8d7da' }} />
            <span>Unpaid</span>
          </div>
          <div className="phone-key-item">
            <span className="phone-key-swatch phone-key-swatch--ring-unallocated" />
            <span>Unallocated</span>
          </div>
          <div className="phone-key-item">
            <span className="phone-key-swatch phone-key-swatch--ring-remaining" />
            <span>Remaining</span>
          </div>
        </div>
      </div>

      <TransferEditFab editTransfers={editTransfers} setEditTransfers={setEditTransfers} />
    </div>
  );
};

export default PhoneLandscapeTransferView;
